/**
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/dashboard/index.tsx — `RecentOrder`'s
 * template slot, unchanged since `018` round 2 and only moved here (008-reporting-dashboards
 * T008/T009) out of `dashboard.tsx`, which carried this same header before the split. Previews the
 * first page of `GET /orders?state=awaiting_approval` through the same `orderColumns`/`MainTable`
 * the real orders list uses — 004's tile, already real.
 */
import { Link } from "react-router-dom";
import { Button, Alert } from "rizzui";
import { WidgetCard, MainTable, useTanStackTable, cn } from "@steelyard/theme";
import { PiSealCheckDuotone as Seal } from "react-icons/pi";
import { TileBoundary } from "./TileBoundary";
import { EmptyTile } from "./TrendStatCard";
import { problemMessage } from "./tone";
import { orderColumns } from "../../pages/orders/columns";
import type { OrderSummary, PagedResult } from "../../lib/services/ordering";

function unreadable(error: unknown) {
  return (
    <WidgetCard title="Orders awaiting approval" className="flex flex-col p-0 lg:p-0" headerClassName="px-5 pt-5 lg:px-7 lg:pt-7 mb-6">
      <div className="px-5 pb-5 lg:px-7 lg:pb-7">
        <Alert color="danger" variant="flat">
          {problemMessage(error, "Could not be read — this is not the same as nothing waiting.")}
        </Alert>
      </div>
    </WidgetCard>
  );
}

function Results({ items }: { items: OrderSummary[] }) {
  const { table } = useTanStackTable<OrderSummary>({
    tableData: items,
    // View only: a preview tile, not the place to reject or cancel.
    columnConfig: orderColumns(false),
    options: { initialState: { pagination: { pageIndex: 0, pageSize: 5 } }, enableColumnResizing: false },
  });
  return (
    <WidgetCard
      title="Orders awaiting approval"
      className={cn("flex flex-col p-0 lg:p-0")}
      headerClassName="px-5 pt-5 lg:px-7 lg:pt-7 mb-6"
      action={
        <Link to="/orders?state=awaiting_approval">
          <Button as="span" variant="outline" size="sm">
            View all
          </Button>
        </Link>
      }
    >
      {items.length === 0 ? (
        <EmptyTile icon={<Seal />} title="Nothing is waiting on an approval right now.">
          Orders held for a Sales or Account Manager appear here.
        </EmptyTile>
      ) : (
        <MainTable table={table} variant="modern" classNames={{ cellClassName: "first:ps-6", headerCellClassName: "first:ps-6" }} />
      )}
    </WidgetCard>
  );
}

export function PendingApprovalsTile({ resolve }: { resolve: Promise<PagedResult<OrderSummary>> }) {
  return (
    <TileBoundary resolve={resolve} errorFallback={unreadable}>
      {(orders) => <Results items={orders.items} />}
    </TileBoundary>
  );
}
